import React, { useEffect, useState } from "react";
import { IWorkTimeFormOptions } from "../../hooks/worktime/useWorkTimeFormOptions";
import MuiSelect, { IMuiSelectOption } from "../framework/muiSelect";
import { FormikProps, useFormik } from "formik";
import { defaultFormikConfig, formikFieldProps } from "../framework/formUtils";
import { IWorkTimeStartFormItem } from "../../models/workShiftTimeSlot/workTimeStartFormItem";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../framework/redux/hooks";
import { setWorkTimeFormItem } from "../../framework/redux/slices/workShiftTimeSlotSlice";
import { useNavigate } from "react-router-dom";
import { MainLayout } from "../layout/mainLayout";
import { PageTitle } from "../layout/pageTitle";
import { SubmitButton } from "../framework/formButtons";
import Grid2 from "@mui/material/Unstable_Grid2";
import { useAppOptionValueEquals } from "../../hooks/framework/useAppOptionValue";
import { optionOwnerHasHourbookingTypeSelectAllowed } from "../../models/common/consts";

export interface IWorkTimeStartFormProps {
    item: IWorkTimeStartFormItem;
    formOptions: IWorkTimeFormOptions;
}

export const WorkTimeStartForm = ({
    item,
    formOptions,
}: IWorkTimeStartFormProps) => {
    const {
        allCostCenters,
        costCenters,
        vehicles,
        salaryRowTypes,
        customers,
        myVehicles,
    } = formOptions;
    const [filteredCostCenters, setFilteredCostCenters] = useState<IMuiSelectOption[]>(costCenters);
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const handleSubmit = (formData: IWorkTimeStartFormItem) => {
        dispatch(setWorkTimeFormItem(formData));
        navigate("/");
    };
    const [hourBookingTypeEnabled] = useAppOptionValueEquals(
        optionOwnerHasHourbookingTypeSelectAllowed,
        "1"
    );

    const formik: FormikProps<IWorkTimeStartFormItem> = useFormik({
        ...defaultFormikConfig,
        initialValues: item,
        onSubmit: handleSubmit,
    });

    useEffect(() => {
        let customer = customers?.find((c) => c.id === formik.values.customerId);

        const costCenter = allCostCenters?.find(
            (c) => c.id === formik.values.costCenterId
        );

        if (customer && customer?.title !== costCenter?.group) {
            formik.setFieldValue("costCenterId", null);
        }

        if (formik.values.customerId) {
            if (customer) {
                setFilteredCostCenters(
                    allCostCenters.filter(cc => cc.group.includes(customer.title))
                );
            }
        } else {
            setFilteredCostCenters(costCenters);
        }
    }, [formik.values.customerId, allCostCenters, customers]);

    useEffect(() => {
        if (formik.values.costCenterId) {
            let costCenter = allCostCenters.find(cc => cc.id === formik.values.costCenterId);
            let customer = customers.find(c => c.title === costCenter?.group);
            if (customer) {
                formik.setFieldValue("customerId", customer.id);
            }
        }
    }, [formik.values.costCenterId]);

    useEffect(() => {
        if (
            !!formik.values.vehicleId &&
            !!myVehicles
        ) {
            const vehicle = myVehicles.find(
                (v) => v.id === formik.values.vehicleId
            );

            if (!!vehicle?.costCenterId) {
                formik.setFieldValue("costCenterId", vehicle.costCenterId);
            }
        }
    }, [formik.values.vehicleId, myVehicles]);

    return (
        <MainLayout
            topComponent={
                <PageTitle header={t("workTime.editAllocationDetails")} />
            }
            hasPx
        >
            <Grid2 container gap={2}>
                <MuiSelect
                    label={t("common.vehicle")}
                    options={vehicles}
                    noSelection
                    {...formikFieldProps(formik, "vehicleId")}
                />
                {customers?.length > 0 && (
                    <MuiSelect
                        label={t("workTime.customer")}
                        options={customers}
                        noSelection
                        {...formikFieldProps(formik, "customerId")}
                    />
                )}
                {allCostCenters?.length > 0 && (
                    <MuiSelect
                        label={t("workTime.costCenter")}
                        options={filteredCostCenters}
                        noSelection
                        {...formikFieldProps(formik, "costCenterId")}
                    />
                )}
                {salaryRowTypes?.length > 0 && hourBookingTypeEnabled && (
                    <MuiSelect
                        label={t("workTime.categoryCustomAddition")}
                        options={salaryRowTypes.map((i) => ({
                            id: i.id,
                            title: i.name,
                        }))}
                        noSelection
                        {...formikFieldProps(formik, "salaryRowTypeId")}
                    />
                )}
                <Grid2 xs={12} display="flex" justifyContent="center">
                    <SubmitButton
                        formik={formik}
                        label={t("common.continue")}
                    />
                </Grid2>
            </Grid2>
        </MainLayout>
    );
};
